import React, { useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { Contribution, ContributionInvestment, Services } from '../../api';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { NPOUser } from 'npo/pages/ReportsBeta/NewProgram/api';
import TrueImpactMultiSelectSearch from 'shared/design-system/components/TrueImpactMultiSelectSearch';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

const getYearsRange = (x: number): number[] => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - x;
  const endYear = currentYear + x;
  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }
  return years;
};
const years = getYearsRange(5);

export interface ContributionDetailsProps {
  services: Services[];
  investmentIndex: number;
  contributionIndex: number;
  contribution: Contribution;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  allPortfolios: RequestState<Contribution[]>;
  investment: ContributionInvestment;
  className?: string;
}

const StyledDiv = styled.div`
  display: flex;
  gap: ${SPACERS.extraLarge};
  .details-label {
    margin-bottom: ${SPACERS.extraTiny};
  }
  .details-left-column {
    flex: 1;
    .dropdown {
      .MuiSelect-select {
        padding-top: 14.5px;
        padding-bottom: 14.5px;
      }
    }
    .dropdown.contribution-year {
      min-width: 107px;
    }
    .dropdown.contribution-manager {
      max-width: 430px;
    }
    .portfolio-year-manager {
      display: flex;
      align-items: center;
      gap: ${SPACERS.small};
      margin-top: ${SPACERS.small};
      .dropdown {
        flex: 1;
      }
    }
  }
  .details-right-column {
    flex: 1;
    .tags-search {
      margin-top: ${SPACERS.medium};
      margin-bottom: ${SPACERS.medium};
      .MuiAutocomplete-inputRoot {
        padding-top: 10px;
        padding-bottom: 10px;
        background-color: ${COLORS.whiteBase};
      }
    }
  }
  @media screen and (max-width: 1400px) {
    display: block;
    .details-left-column {
      margin-bottom: ${SPACERS.extraLarge};
    }
  }
`;

const ContributionDetails: React.FC<ContributionDetailsProps> = ({
  services,
  contribution,
  investment,
  investmentIndex,
  allNPOUsers,
  allPortfolios,
  className,
}) => {
  const portfolioOptions = allPortfolios.data || [];
  const userOptions = allNPOUsers.data?.allUsers || [];
  const [tags, setTags] = useState<Services[]>([]);
  const [year, setYear] = useState<number>(contribution.year);
  const [portfolio, setPortfolio] = useState<Contribution>(contribution);
  const [manager, setManager] = useState<NPOUser | undefined>(
    userOptions.find(x => x.dbId === contribution.managerId),
  );

  return (
    <StyledDiv className={className}>
      <div className="details-left-column">
        <div className="dropdown">
          <TrueImpactText variant="body-bold" className="details-label">
            Portfolio
          </TrueImpactText>
          <TrueImpactDropdown
            native={true}
            options={portfolioOptions}
            value={portfolio}
            getId={c => c.id}
            getLabel={c => c.name}
            name="Select Portfolio"
            getItemDisability={() => false}
            onChange={setPortfolio}
            className="dropdown"
          />
        </div>
        <div className="portfolio-year-manager">
          <div className="dropdown contribution-year">
            <TrueImpactText variant="body-bold" className="details-label">
              Portfolio Year
            </TrueImpactText>
            <TrueImpactDropdown
              native={true}
              options={years}
              value={year}
              getId={y => y}
              getLabel={y => y}
              name="Select Year"
              getItemDisability={() => false}
              onChange={setYear}
              className="dropdown"
            />
          </div>
          <div className="dropdown contribution-manager">
            <TrueImpactText variant="body-bold" className="details-label">
              Manager
            </TrueImpactText>
            <TrueImpactDropdown
              native={true}
              options={userOptions}
              value={manager}
              getId={c => c.dbId}
              getLabel={c => `${c.firstName} ${c.lastName} (${c.email})`}
              name="Select Manager"
              getItemDisability={() => false}
              onChange={setManager}
              className="dropdown"
            />
          </div>
        </div>
      </div>
      <div className="details-right-column">
        <TrueImpactText variant="body-bold">Portfolio tags</TrueImpactText>
        <TrueImpactText>
          Tags are used to Duis mollis, est non commodo luctus, nisi erat
          porttitor ligula, eget lacinia odio sem nec elit.
        </TrueImpactText>
        <TrueImpactMultiSelectSearch
          options={services}
          selected={[]}
          onChange={setTags}
          getId={o => o.dbId}
          renderOption={o => o.name}
          placeholder="Search Tags"
          clearAllText="Clear all tags"
          compare={(o1, o2) => o1.dbId === o2.dbId}
          filterOptions={(options, state) => {
            const filtered = options.filter(o =>
              state.inputValue
                ? o.name.toLowerCase().includes(state.inputValue.toLowerCase())
                : true,
            );
            return filtered;
          }}
          className="tags-search"
        />
      </div>
    </StyledDiv>
  );
};

export default ContributionDetails;
